import * as React from 'react';
import { useParams } from 'react-router';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import ModalDialog from 'components/ModalDialog';
import { tenantConfig } from 'utils/tenant';
import { userSession } from 'state/session';
import { formatDateDisplay, formatTimeDisplay } from 'utils/date';

export default function PrescheduledRoute() {
  const { brand, market, sessionId } = useParams<{ brand: string; market: string; sessionId: string }>();
  const { _ } = useLingui();
  const session = userSession().useState();
  tenantConfig().useState();

  const scheduledStartTime = session.scheduledStartTime;
  const { startWindowBeforeMinutes: startWindowBefore } = tenantConfig().getScheduler();

  // Calculate the date/time display
  const scheduledDate = React.useMemo(
    () => (scheduledStartTime ? new Date(scheduledStartTime) : new Date()),
    [scheduledStartTime]
  );
  const formattedDate = formatDateDisplay(scheduledDate, _);
  const formattedTime = formatTimeDisplay(scheduledDate);

  // Calculate earliest start time (recalculates when startWindowBefore changes)
  const earliestStartTime = React.useMemo(
    () => new Date(scheduledDate.getTime() - startWindowBefore * 60 * 1000),
    [scheduledDate, startWindowBefore]
  );
  const earliestFormattedDate = formatDateDisplay(earliestStartTime, _);
  const earliestFormattedTime = formatTimeDisplay(earliestStartTime);

  // Check if we're within the start window
  const [canStart, setCanStart] = React.useState(() => {
    const now = new Date();
    return now >= earliestStartTime;
  });

  // Recalculate canStart when earliestStartTime changes
  React.useEffect(() => {
    const now = new Date();
    setCanStart(now >= earliestStartTime);
  }, [earliestStartTime]);

  // Poll every 10 seconds to check if we've entered the start window
  React.useEffect(() => {
    const checkTimeWindow = () => {
      const now = new Date();
      if (now >= earliestStartTime) {
        setCanStart(true);
      }
    };

    // If already in window, no need to poll
    if (canStart) {
      return;
    }

    // Check every 10 seconds
    const intervalId = setInterval(checkTimeWindow, 10000);

    return () => clearInterval(intervalId);
  }, [canStart, earliestStartTime]);

  // Message stays constant
  const message = _(
    msg`This session is scheduled for ${formattedDate} at ${formattedTime}. You can start it ${startWindowBefore} minutes before the scheduled time.`
  );

  // Link text changes based on whether we can start
  const linkText = canStart ? _(msg`Start`) : _(msg`Continue at ${earliestFormattedTime} ${earliestFormattedDate}`);

  return (
    <ModalDialog
      title={_(msg`Session Scheduled`)}
      message={message}
      icon="ex3d-icon--calendar"
      linkText={linkText}
      to={`/${brand}/${market}/${sessionId}`}
      disabled={!canStart}
      data-testid="prescheduled-modal"
    />
  );
}
